import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, AlertTriangle } from 'lucide-react';
import { useOnboarding } from '../../hooks/useOnboarding';

const BUDGET_PRESETS = [
  { amount: 150000, label: 'Básico', description: 'Para gastos esenciales' },
  { amount: 300000, label: 'Moderado', description: 'Balance entre necesidades y gustos' },
  { amount: 500000, label: 'Cómodo', description: 'Para un estilo de vida relajado' },
  { amount: 800000, label: 'Amplio', description: 'Sin restricciones importantes' }
];

export const BudgetStep: React.FC = () => {
  const { userPreferences, saveUserPreferences } = useOnboarding();
  const [monthlyBudget, setMonthlyBudget] = useState<number>(0);
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [isCustom, setIsCustom] = useState(false);

  // Cargar presupuesto desde las preferencias
  useEffect(() => {
    if (userPreferences?.monthlyBudget) {
      setMonthlyBudget(userPreferences.monthlyBudget);
      
      // Verificar si coincide con algún preset
      const presetMatch = BUDGET_PRESETS.find(preset => preset.amount === userPreferences.monthlyBudget);
      if (presetMatch) {
        setSelectedPreset(userPreferences.monthlyBudget);
        setIsCustom(false);
      } else {
        setIsCustom(true);
      }
    }
  }, [userPreferences]);

  const handlePresetSelect = (amount: number) => {
    setSelectedPreset(amount);
    setMonthlyBudget(amount);
    setIsCustom(false);
    saveUserPreferences({ monthlyBudget: amount });
  };

  const handleCustomBudget = (value: string) => {
    const amount = parseFloat(value) || 0;
    setMonthlyBudget(amount);
    setSelectedPreset(null);
    setIsCustom(true);
    
    // Guardar solo si es un valor válido
    if (amount > 0) {
      saveUserPreferences({ monthlyBudget: amount });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const getBudgetAdvice = (amount: number) => {
    if (amount === 0) return null;
    
    if (amount < 150000) {
      return {
        type: 'warning',
        message: 'Este presupuesto es muy ajustado. Asegúrate de priorizar gastos esenciales.',
        icon: <AlertTriangle className="w-4 h-4 text-yellow-600" />
      };
    } else if (amount >= 150000 && amount <= 400000) {
      return {
        type: 'good',
        message: 'Buen presupuesto para cubrir necesidades básicas y algunos gustos.',
        icon: <TrendingUp className="w-4 h-4 text-green-600" />
      };
    } else if (amount > 400000 && amount <= 700000) {
      return {
        type: 'great',
        message: 'Excelente presupuesto que te permite flexibilidad y ahorros.',
        icon: <TrendingUp className="w-4 h-4 text-blue-600" />
      };
    } else {
      return {
        type: 'high',
        message: 'Presupuesto amplio. Considera destinar parte a inversiones o ahorros.',
        icon: <DollarSign className="w-4 h-4 text-purple-600" />
      };
    }
  };

  const advice = getBudgetAdvice(monthlyBudget);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h4 className="text-lg font-semibold text-gray-900 mb-2">
          ¿Cuál es tu presupuesto mensual?
        </h4>
        <p className="text-gray-600">
          Esto nos ayudará a darte mejores recomendaciones y alertas personalizadas
        </p>
      </div>

      {/* Presets de presupuesto */}
      <div>
        <h5 className="font-medium text-gray-900 mb-3">
          Presupuestos sugeridos
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {BUDGET_PRESETS.map((preset) => (
            <button
              key={preset.amount}
              onClick={() => handlePresetSelect(preset.amount)}
              className={`p-4 rounded-lg border text-left transition-all ${
                selectedPreset === preset.amount
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-lg">
                  {formatCurrency(preset.amount)}
                </span>
                <span className={`text-sm px-2 py-1 rounded ${
                  selectedPreset === preset.amount
                    ? 'bg-blue-200 text-blue-800'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {preset.label}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {preset.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Presupuesto personalizado */}
      <div>
        <h5 className="font-medium text-gray-900 mb-3">
          O ingresa un monto personalizado
        </h5>
        <div className="relative">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <span className="text-gray-500">€</span>
          </div>
          <input
            type="number"
            min="0"
            step="50"
            value={isCustom ? monthlyBudget || '' : ''}
            onChange={(e) => handleCustomBudget(e.target.value)}
            placeholder="0.00"
            className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              isCustom ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
          />
        </div>
      </div>

      {/* Consejo basado en el presupuesto */}
      {advice && (
        <div className={`p-4 rounded-lg border ${
          advice.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
          advice.type === 'good' ? 'bg-green-50 border-green-200' :
          advice.type === 'great' ? 'bg-blue-50 border-blue-200' :
          'bg-purple-50 border-purple-200'
        }`}>
          <div className="flex items-start space-x-3">
            {advice.icon}
            <div>
              <h6 className="font-medium text-gray-900 mb-1">
                Consejo personalizado
              </h6>
              <p className="text-sm text-gray-700">
                {advice.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Información adicional */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h6 className="font-medium text-gray-900 mb-2">
          ¿Por qué es importante?
        </h6>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Te ayudamos a mantener tus gastos bajo control</li>
          <li>• Recibirás alertas cuando te acerques a tu límite</li>
          <li>• Podrás ver qué porcentaje de tu presupuesto has usado</li>
          <li>• Puedes cambiar este valor en cualquier momento</li>
        </ul>
      </div>

      {/* Resumen */}
      {monthlyBudget > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h6 className="font-medium text-blue-900">
                Presupuesto mensual establecido
              </h6>
              <p className="text-sm text-blue-700">
                Podrás crear presupuestos específicos por categoría más adelante
              </p>
            </div>
            <div className="text-2xl font-bold text-blue-900">
              {formatCurrency(monthlyBudget)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};